import R from './ramda'

class Utils {
  // Utility per caricare dati JSON e comunicare al componente l'esito
  // dell'operazione.
  //
  // La callback viene invocata con i dati ricevuti oppure con {error: '...'}
  // e normalmente salva l'oggetto ricevuto in un elemento dello state.
  //
  // NB. il valore {loading: true} viene impostato dal chiamante prima di invocare
  // loadJsonData(), perché questa chiamata potrebbe far parte di un setState()
  // e non si possono annidare setState()
  //
  static loadJsonData(url, callback) {
    fetch(url, {
      method: 'GET',
      mode: 'cors',
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json()
        }
        throw new Error(`HTTP ${resp.status} ${resp.statusText}`)
      })
      .then((json) => {
        callback(json)
      })
      .catch((err) => {
        console.error(err)
        callback({ error: err.message })
      })
    // TODO abort https://github.com/w3c/web-platform-tests/pull/6484#issuecomment-315775251
  }

  // Ritorna una funzione che imposta delle chiavi in un oggetto.
  //
  // Può essere chiamato con un object di chiavi/valori da settare,
  // oppure con due argomenti chiave e valore distinti.
  //
  static set(keys, value) {
    if (R.is(Object, keys)) {
      return R.mergeWith(R.nthArg(0), keys)
    } else {
      return R.set(R.lensProp(keys), value)
    }
  }

  // Ritorna una funzione che rimuove delle chiavi da un oggetto.
  //
  // Può essere chiamato con un array o set di chiavi da rimuovere,
  // oppure con una singola chiave da rimuovere.
  static unset(keys) {
    if (!R.is(Array, keys) && !R.is(Set, keys)) {
      keys = [keys]
    }
    let key_set = new Set(R.map((x) => x.toString(), keys))
    return R.pickBy((v, k) => !key_set.has(k))
  }

  // Formatta un numero in stile italiano, con punti delle migliaia,
  // virgola decimale e segno meno tipografico
  static formatNumber(n) {
    return (n || 0)
      .toString()
      .replace('.', ',')
      .replace('-', '\u2212')
      .replace(/\d(?=(?:\d\d\d)+$)/g, '$&.')
  }

  // Come formatNumber, ma aggiunge un '+' se il numero non è negativo
  static formatNumberPlus(n) {
    return (n >= 0 ? '+' : '') + Utils.formatNumber(n)
  }

  // Formatta una label troncandola a tot caratteri
  static formatLabel(s) {
    const max_len = 40
    if (s.length <= max_len) return s
    s = s.substr(0, max_len)
    if (s.lastIndexOf(' ')) s = s.substr(0, s.lastIndexOf(' '))
    return s + '\u2026'
  }

  // Crea una mappa {[id]: lev} contenente i filtri di un menu multi-livello,
  // che sono memorizzati in props.filters a livelli separati. Ad es, dato:
  //   filters = {
  //     prof_1: [18,19],
  //     prof_2: [34],
  //     ...
  //   }
  // si ha:
  //   extract_multilevel(filters, 'prof') = {
  //     18: 1,
  //     19: 1,
  //     34: 2,
  //   }
  static extract_multilevel(filters, name) {
    let values = {}
    for (let key in filters) {
      if (!filters.hasOwnProperty(key)) continue
      let m = key.match(`^${name}_(\\d+)$`)
      if (m) {
        let lev = parseInt(m[1], 10)
        filters[key].forEach((id) => {
          values[id] = lev
        })
      }
    }
    return values
  }

  // Crea un updater per i filtri di un menu multi-livello.
  //
  // A partire dal nome del filtro crea una funzione che accetta:
  //  - values: una mappa da id a livello
  //    (come quella ritornata da extract_multilevel)
  //  - descr: lista di descrizioni testuali
  // e invoca onUpdate() con una funzione che riscrive i filtri interessati.
  static multilevel_updater(onUpdate, name) {
    return (values, descr) =>
      onUpdate(
        // filters updater
        (prevFilters) => {
          let filters = R.pickBy((v, k) => !k.match(`^${name}_`))(prevFilters)
          for (let id in values) {
            if (!values.hasOwnProperty(id)) continue
            let lev = values[id]
            let key = `${name}_${lev}`
            if (!filters[key]) filters[key] = []
            filters[key].push(id)
          }
          return filters
        },
        // filterDescr updater
        descr ? Utils.set(name, descr) : Utils.unset(name),
      )
  }

  // Rimuove tutti i filtri indicati
  static clear_filters(onUpdate, names) {
    onUpdate(
      R.pickBy((v, k) => !R.any((name) => k.match(`^${name}(_|$)`), names)),
      Utils.unset(names),
    )
  }
}

export default Utils



// WEBPACK FOOTER //
// ./src/Utils.js